import { useState, useEffect } from 'react';
import {
  Text,
  Input,
  Button,
  Spinner,
  Avatar,
  Badge,
  Select,
  Switch,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Eye24Regular,
  EyeOff24Regular,
  Checkmark24Regular,
  ShieldCheckmark24Regular,
  ShieldDismiss24Regular,
  Star24Regular,
} from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { apiFetch } from '../lib/apiFetch';

const LOCALES = [
  { value: '', label: 'Default' },
  { value: 'en', label: 'English' },
  { value: 'en-CA', label: 'English (Canada)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'fr', label: 'French' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'nl', label: 'Dutch' },
];

const TIMEZONES = [
  { value: '', label: 'Default' },
  { value: 'America/St_Johns', label: 'Newfoundland (NST)' },
  { value: 'America/Halifax', label: 'Atlantic (AST)' },
  { value: 'America/Toronto', label: 'Eastern (EST)' },
  { value: 'America/Chicago', label: 'Central (CST)' },
  { value: 'America/Denver', label: 'Mountain (MST)' },
  { value: 'America/Edmonton', label: 'Mountain - Canada (MST)' },
  { value: 'America/Vancouver', label: 'Pacific (PST)' },
  { value: 'UTC', label: 'UTC' },
];

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '8px',
    ...shorthands.padding('16px'),
    overflowY: 'auto',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('24px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    maxWidth: '560px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    display: 'block',
  },
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  avatarMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  avatarName: {
    fontSize: '18px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  avatarEmail: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  badgeRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground2,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
  },
  divider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
  },
  successMsg: {
    fontSize: '13px',
    color: tokens.colorStatusSuccessForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  errorMsg: {
    fontSize: '13px',
    color: tokens.colorStatusDangerForeground1,
  },
  pendingMsg: {
    fontSize: '12px',
    color: tokens.colorPalettePumpkinBorderActive,
  },
  subForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '4px',
  },
  statusRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('10px', '14px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  gravatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  gravatarMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  gravatarDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  statusText: {
    display: 'flex',
    flexDirection: 'column',
  },
  statusLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  statusDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
});

interface ProfileData {
  firstname: string;
  lastname: string;
  locale: string;
  timezone: string;
  avatar_url: string | null;
  owner: boolean;
  confirmed: boolean;
  has_two_factor_autentication: boolean;
  permissions: string[];
  phone: string;
  job_title: string;
  department: string;
  default_location_id: string | null;
  driver_license_number: string;
  driver_license_expiry: string | null;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  notification_preferences: Record<string, any>;
  default_view: string;
  gravatar_email: string | null;
  unconfirmed_email: string | null;
}

export default function Profile() {
  const styles = useStyles();
  const { user, token, updateUser } = useAdminAuth();

  const [profile, setProfile] = useState<ProfileData | null>(null);

  // Personal info
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [locale, setLocale] = useState('');
  const [timezone, setTimezone] = useState('');

  // Operational
  const [licenseNumber, setLicenseNumber] = useState('');
  const [licenseExpiry, setLicenseExpiry] = useState('');
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');

  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Gravatar
  const [gravatarEnabled, setGravatarEnabled] = useState(false);
  const [gravatarEmail, setGravatarEmail] = useState('');
  const [gravatarPreviewUrl, setGravatarPreviewUrl] = useState<string | null>(null);

  // Notification preferences
  const [notifNewBookingEmail, setNotifNewBookingEmail] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifSuccess, setNotifSuccess] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  // Email change (#43) — independent of the profile "Save changes" button, since
  // the email swap only completes after the employee clicks the verification link.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Password
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    const email = (gravatarEmail || user?.email || '').trim().toLowerCase();
    if (!email) return;
    crypto.subtle.digest('SHA-256', new TextEncoder().encode(email)).then(buf => {
      const hex = Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
      setGravatarPreviewUrl(`https://www.gravatar.com/avatar/${hex}?s=128&d=mp`);
    });
  }, [gravatarEmail, user?.email]);

  useEffect(() => {
    if (!token) return;
    apiFetch(`/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then((data: ProfileData) => {
        setProfile(data);
        setFirstname(data.firstname || '');
        setLastname(data.lastname || '');
        setPhone(data.phone || '');
        setJobTitle(data.job_title || '');
        setDepartment(data.department || '');
        setLocale(data.locale || '');
        setTimezone(data.timezone || '');
        setLicenseNumber(data.driver_license_number || '');
        setLicenseExpiry(data.driver_license_expiry || '');
        setEmergencyName(data.emergency_contact_name || '');
        setEmergencyPhone(data.emergency_contact_phone || '');
        setGravatarEnabled(!!(data.avatar_url && data.avatar_url.includes('gravatar.com')));
        setGravatarEmail(data.gravatar_email || '');
        const notifPrefs = data.notification_preferences || {};
        setNotifNewBookingEmail(!!notifPrefs.new_booking_email);
        setUnconfirmedEmail(data.unconfirmed_email || null);
      })
      .catch((err: Error) => setProfileError(err.message || 'Failed to load profile'));
  }, [token]);

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileError(null);
    setProfileSuccess(false);
    try {
      const res = await apiFetch(`/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          firstname, lastname, phone, job_title: jobTitle, department,
          locale, timezone,
          driver_license_number: licenseNumber,
          driver_license_expiry: licenseExpiry || null,
          emergency_contact_name: emergencyName,
          emergency_contact_phone: emergencyPhone,
          avatar_url: gravatarEnabled ? gravatarPreviewUrl : null,
          gravatar_email: gravatarEnabled && gravatarEmail ? gravatarEmail : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      const newAvatarUrl = gravatarEnabled ? gravatarPreviewUrl : null;
      updateUser({ avatar_url: newAvatarUrl, name: `${firstname} ${lastname}`.trim() || user?.name || '' });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (e: any) {
      setProfileError(e.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  const handleEmailChange = async () => {
    setEmailError(null);
    setEmailMsg(null);
    if (!newEmail.trim()) { setEmailError('Enter a new email address'); return; }
    if (!emailPassword) { setEmailError('Enter your current password to confirm'); return; }
    setEmailSaving(true);
    try {
      const res = await apiFetch(`/auth/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_email: newEmail.trim(), current_password: emailPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setEmailMsg(data.message);
      setUnconfirmedEmail(data.unconfirmed_email || newEmail.trim());
      setNewEmail('');
      setEmailPassword('');
      setShowEmailForm(false);
    } catch (e: any) {
      setEmailError(e.message || 'Failed to start email change');
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);
    if (newPassword !== confirmPassword) { setPasswordError('New passwords do not match'); return; }
    if (newPassword.length < 8) { setPasswordError('Password must be at least 8 characters'); return; }
    setPasswordSaving(true);
    try {
      const res = await apiFetch(`/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setPasswordSuccess(true);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (e: any) {
      setPasswordError(e.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleNotifSave = async () => {
    setNotifSaving(true);
    setNotifError(null);
    setNotifSuccess(false);
    try {
      const res = await apiFetch(`/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          notification_preferences: {
            new_booking_email: notifNewBookingEmail,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setNotifSuccess(true);
      setTimeout(() => setNotifSuccess(false), 3000);
    } catch (e: any) {
      setNotifError(e.message || 'Failed to save');
    } finally {
      setNotifSaving(false);
    }
  };

  const saveActions = (success: boolean, error: string | null, saving: boolean, onSave: () => void) => (
    <>
      {error && <Text className={styles.errorMsg}>{error}</Text>}
      <div className={styles.actions}>
        {success && <Text className={styles.successMsg}><Checkmark24Regular fontSize={16} /> Saved</Text>}
        <Button appearance="primary" onClick={onSave} disabled={saving}>
          {saving ? <Spinner size="tiny" /> : 'Save changes'}
        </Button>
      </div>
    </>
  );

  return (
    <div className={styles.page}>

      {/* ── Identity card ── */}
      <div className={styles.card}>
        <div className={styles.avatarRow}>
          <Avatar
            name={user?.name || ''}
            image={(gravatarEnabled && gravatarPreviewUrl) ? { src: gravatarPreviewUrl } : (profile?.avatar_url ? { src: profile.avatar_url } : undefined)}
            size={64}
          />
          <div className={styles.avatarMeta}>
            <Text className={styles.avatarName}>{user?.name || '—'}</Text>
            <Text className={styles.avatarEmail}>{user?.email}</Text>
            <div className={styles.badgeRow}>
              {profile?.owner && <Badge appearance="filled" color="warning" icon={<Star24Regular />}>Owner</Badge>}
              {profile?.confirmed && <Badge appearance="tint" color="success">Confirmed</Badge>}
            </div>
          </div>
        </div>

        <div className={styles.gravatarRow}>
          {gravatarPreviewUrl && (
            <Avatar image={{ src: gravatarPreviewUrl }} name={user?.name || ''} size={32} />
          )}
          <div className={styles.gravatarMeta}>
            <Switch
              label="Use Gravatar"
              checked={gravatarEnabled}
              onChange={(_, d) => setGravatarEnabled(d.checked)}
            />
            <Text className={styles.gravatarDesc}>Avatar from gravatar.com based on your email address</Text>
          </div>
        </div>
        {gravatarEnabled && (
          <div className={styles.field}>
            <label className={styles.label}>Gravatar email</label>
            <Input
              value={gravatarEmail}
              onChange={(_, d) => setGravatarEmail(d.value)}
              placeholder={user?.email || 'email@example.com'}
              type="email"
            />
            <Text className={styles.gravatarDesc}>Leave blank to use your account email</Text>
          </div>
        )}

        <div className={styles.divider} />
        <Text className={styles.cardTitle}>Personal information</Text>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>First name</label>
            <Input value={firstname} onChange={(_, d) => setFirstname(d.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Last name</label>
            <Input value={lastname} onChange={(_, d) => setLastname(d.value)} />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Email</label>
          <Input value={user?.email || ''} disabled />
          {unconfirmedEmail && (
            <Text className={styles.pendingMsg}>
              Pending confirmation: {unconfirmedEmail} — open the link we emailed to that address to finish the change.
            </Text>
          )}
          {emailMsg && !showEmailForm && (
            <Text className={styles.successMsg}><Checkmark24Regular fontSize={16} /> {emailMsg}</Text>
          )}
          {!showEmailForm ? (
            <div>
              <Button
                appearance="secondary"
                size="small"
                onClick={() => { setShowEmailForm(true); setEmailMsg(null); setEmailError(null); }}
              >
                Change email
              </Button>
            </div>
          ) : (
            <div className={styles.subForm}>
              <div className={styles.field}>
                <label className={styles.label}>New email</label>
                <Input type="email" value={newEmail} onChange={(_, d) => setNewEmail(d.value)} placeholder="you@example.com" />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Current password</label>
                <Input type="password" value={emailPassword} onChange={(_, d) => setEmailPassword(d.value)} placeholder="Confirm it's you" />
              </div>
              {emailError && <Text className={styles.errorMsg}>{emailError}</Text>}
              <div className={styles.actions}>
                <Button
                  appearance="secondary"
                  onClick={() => { setShowEmailForm(false); setNewEmail(''); setEmailPassword(''); setEmailError(null); }}
                  disabled={emailSaving}
                >
                  Cancel
                </Button>
                <Button appearance="primary" onClick={handleEmailChange} disabled={emailSaving}>
                  {emailSaving ? <Spinner size="tiny" /> : 'Send verification'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>Phone</label>
            <Input value={phone} onChange={(_, d) => setPhone(d.value)} placeholder="+1 (555) 000-0000" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Job title</label>
            <Input value={jobTitle} onChange={(_, d) => setJobTitle(d.value)} placeholder="e.g. Dispatcher" />
          </div>
        </div>

        <div className={styles.field}>
          <label className={styles.label}>Department</label>
          <Input value={department} onChange={(_, d) => setDepartment(d.value)} placeholder="e.g. Operations" />
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>Locale</label>
            <Select value={locale} onChange={(_, d) => setLocale(d.value)}>
              {LOCALES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </Select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Timezone</label>
            <Select value={timezone} onChange={(_, d) => setTimezone(d.value)}>
              {TIMEZONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Select>
          </div>
        </div>

        {saveActions(profileSuccess, profileError, profileSaving, handleProfileSave)}
      </div>

      {/* ── Operational card ── */}
      <div className={styles.card}>
        <Text className={styles.cardTitle}>Operational</Text>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>Driver license #</label>
            <Input value={licenseNumber} onChange={(_, d) => setLicenseNumber(d.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>License expiry</label>
            <Input type="date" value={licenseExpiry} onChange={(_, d) => setLicenseExpiry(d.value)} />
          </div>
        </div>

        <div className={styles.divider} />
        <Text className={styles.cardTitle}>Emergency contact</Text>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>Name</label>
            <Input value={emergencyName} onChange={(_, d) => setEmergencyName(d.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Phone</label>
            <Input value={emergencyPhone} onChange={(_, d) => setEmergencyPhone(d.value)} placeholder="+1 (555) 000-0000" />
          </div>
        </div>

        {saveActions(profileSuccess, profileError, profileSaving, handleProfileSave)}
      </div>

      {/* ── Security card ── */}
      <div className={styles.card}>
        <Text className={styles.cardTitle}>Security</Text>
        <div className={styles.statusRow}>
          <div className={styles.statusItem}>
            <div className={styles.statusLeft}>
              {profile?.has_two_factor_autentication
                ? <ShieldCheckmark24Regular style={{ color: tokens.colorStatusSuccessForeground1 }} />
                : <ShieldDismiss24Regular style={{ color: tokens.colorStatusDangerForeground1 }} />}
              <div className={styles.statusText}>
                <Text className={styles.statusLabel}>Two-factor authentication</Text>
                <Text className={styles.statusDesc}>
                  {profile?.has_two_factor_autentication ? 'Enabled via Booqable' : 'Not enabled — configure in Booqable'}
                </Text>
              </div>
            </div>
            <Badge appearance="tint" color={profile?.has_two_factor_autentication ? 'success' : 'danger'}>
              {profile?.has_two_factor_autentication ? 'On' : 'Off'}
            </Badge>
          </div>
        </div>
      </div>

      {/* ── Notifications card ── */}
      <div className={styles.card}>
        <Text className={styles.cardTitle}>Notifications</Text>
        <div className={styles.statusRow}>
          <div className={styles.statusItem}>
            <div className={styles.statusLeft}>
              <div className={styles.statusText}>
                <Text className={styles.statusLabel}>New online booking</Text>
                <Text className={styles.statusDesc}>Receive an email when a customer completes a booking on the website</Text>
              </div>
            </div>
            <Switch
              checked={notifNewBookingEmail}
              onChange={(_, d) => setNotifNewBookingEmail(d.checked)}
            />
          </div>
        </div>
        {saveActions(notifSuccess, notifError, notifSaving, handleNotifSave)}
      </div>

      {/* ── Change password card ── */}
      <div className={styles.card}>
        <Text className={styles.cardTitle}>Change password</Text>

        <div className={styles.field}>
          <label className={styles.label}>Current password</label>
          <Input
            type={showCurrent ? 'text' : 'password'}
            value={currentPassword}
            onChange={(_, d) => setCurrentPassword(d.value)}
            contentAfter={
              <Button appearance="transparent" size="small" type="button"
                icon={showCurrent ? <EyeOff24Regular /> : <Eye24Regular />}
                onClick={() => setShowCurrent(v => !v)} />
            }
          />
        </div>

        <div className={styles.fieldRow}>
          <div className={styles.field}>
            <label className={styles.label}>New password</label>
            <Input
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(_, d) => setNewPassword(d.value)}
              contentAfter={
                <Button appearance="transparent" size="small" type="button"
                  icon={showNew ? <EyeOff24Regular /> : <Eye24Regular />}
                  onClick={() => setShowNew(v => !v)} />
              }
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Confirm new password</label>
            <Input type="password" value={confirmPassword} onChange={(_, d) => setConfirmPassword(d.value)} />
          </div>
        </div>

        {passwordError && <Text className={styles.errorMsg}>{passwordError}</Text>}
        <div className={styles.actions}>
          {passwordSuccess && <Text className={styles.successMsg}><Checkmark24Regular fontSize={16} /> Password changed</Text>}
          <Button appearance="primary" onClick={handlePasswordChange} disabled={passwordSaving}>
            {passwordSaving ? <Spinner size="tiny" /> : 'Change password'}
          </Button>
        </div>
      </div>
    </div>
  );
}
